import React, { forwardRef, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Virtual } from 'swiper/modules';
import HeldPicture from './HeldPicture';
import ImageCaption from './ImageCaption';
import MediaFrame from './MediaFrame';
import SlideshowText from '@components/page/SlideshowText';
import SpreadSlide from './SpreadSlide';
import SpreadReadingSlide from './SpreadReadingSlide';
import SpreadBoxes from '@components/shared/SpreadBoxes';
import ImageCounter from './ImageCounter';
import { FADE_DURATION, SWIPE_CONFIG } from '../imageViewerUtils';

import 'swiper/css';
import 'swiper/css/virtual';

/**
 * A phone picture with the blocks kept on its page, laid over the picture as
 * it is fitted to the screen.
 */
function PictureWithBoxes({ slide, ratio: pageRatio, children }) {
    const { width, height } = slide.file?.dimensions || {};
    const ratio = width > 0 && height > 0 ? width / height : (pageRatio || 1);
    return (
        <div className="flex h-full w-full items-center justify-center" style={{ containerType: 'size' }}>
            <div
                className="relative isolate flex items-center justify-center overflow-hidden"
                style={{ aspectRatio: ratio, width: `min(100cqw, ${ratio} * 100cqh)` }}
            >
                {children}
                <SpreadBoxes boxes={slide.boxes} single reference={slide.boxReference || 1} />
            </div>
        </div>
    );
}

PictureWithBoxes.propTypes = {
    slide: PropTypes.object.isRequired,
    /** Width over height when the slide has no picture (a blank page). */
    ratio: PropTypes.number,
    children: PropTypes.node,
};

/**
 * Touch-enabled swiper for mobile/tablet image navigation
 * Uses Virtual module to only render ~5 slides at a time
 * (prevents iOS memory crashes with 130+ images)
 * 
 * Supports:
 * - text slides (type: 'text') for editorial content as first slide
 * - spread slides (type: 'spread') for iPad/tablet spread views
 * - image slides (type: 'image') for individual images
 */
const TouchSwiper = forwardRef(({ 
    slidesData, 
    initialSlide, 
    onSlideChange,
    defaultAlt,
    swipeWrapperRef,
    closeAnimationRef,
    pages,
    files,
    history,
    pageDimensions,
    align = 'mc',
    fillScreen = false,
    spreadGap = 0,
    isLandscape = false,
    siteTitle = '',
    onCloseOnLastSlide,
    isEditorial = false,
    currentSlideIndex = null,
    flatImages = [],
    textPage = null,
    theme = null,
    counterCorner = 'br',
}, swiperRef) => {
    // Track current slide index from swiper
    const [activeSlideIndex, setActiveSlideIndex] = useState(initialSlide);
    // Track swiper instance to control navigation
    const swiperInstanceRef = useRef(null);
    // Use closeAnimationRef for close swipe animation, fallback to swipeWrapperRef
    const effectiveCloseRef = closeAnimationRef || swipeWrapperRef;
    const touchStartRef = useRef(null);
    const isCloseSwipingRef = useRef(false);
    const horizontalLockRef = useRef(false);
    const animateBackTimeoutRef = useRef(null);
    
    // Handle touch start to track initial position
    const handleTouchStart = useCallback((swiper, event) => {
        // Swiper may pass PointerEvent (pointerdown) or TouchEvent (touchstart)
        // Handle both: PointerEvent has clientX/clientY directly, TouchEvent has touches array
        let startX, startY;
        if (event.type === 'pointerdown' || event.type.startsWith('pointer')) {
            // PointerEvent - use clientX/clientY directly
            if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
                startX = event.clientX;
                startY = event.clientY;
            } else {
                return;
            }
        } else {
            // TouchEvent - get from touches array
            const touchEvent = event.touches?.[0] || event.targetTouches?.[0] || (event.originalEvent?.touches?.[0]) || null;
            if (!touchEvent) {
                return;
            }
            startX = touchEvent.clientX;
            startY = touchEvent.clientY;
        }
        
        // Clear any pending animation
        if (animateBackTimeoutRef.current) {
            clearTimeout(animateBackTimeoutRef.current);
            animateBackTimeoutRef.current = null;
        }
        if (effectiveCloseRef?.current) {
            effectiveCloseRef.current.style.transition = '';
            effectiveCloseRef.current.style.transform = '';
            effectiveCloseRef.current.style.opacity = '';
        }
        
        touchStartRef.current = {
            x: startX,
            y: startY,
        };
        isCloseSwipingRef.current = false;
        horizontalLockRef.current = false;
    }, [effectiveCloseRef, slidesData.length, onCloseOnLastSlide]);
    
    // Handle touch move to apply scale/fade effect on last slide
    const handleTouchMove = useCallback((swiper, event) => {
        if (!touchStartRef.current) return;
        
        // Swiper may pass PointerEvent or TouchEvent - handle both
        let currentX, currentY;
        if (event.type === 'pointermove' || event.type.startsWith('pointer')) {
            // PointerEvent - use clientX/clientY directly
            if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
                currentX = event.clientX;
                currentY = event.clientY;
            } else {
                return;
            }
        } else {
            // TouchEvent - get from touches array
            const touchEvent = event.touches?.[0] || event.targetTouches?.[0] || (event.originalEvent?.touches?.[0]) || null;
            if (!touchEvent) return;
            currentX = touchEvent.clientX;
            currentY = touchEvent.clientY;
        }
        
        // Check if we're on the last slide
        const isOnLastSlide = swiper.isEnd || (swiper.activeIndex >= slidesData.length - 1);
        if (!onCloseOnLastSlide || !isOnLastSlide) return;
        
        const deltaX = currentX - touchStartRef.current.x;
        const deltaY = currentY - touchStartRef.current.y;
        
        // Lock to horizontal if horizontal movement is dominant (swiping left/next direction)
        if (!horizontalLockRef.current) {
            if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX < -10) {
                horizontalLockRef.current = true;
                isCloseSwipingRef.current = true;
                // Prevent Swiper from handling this swipe
                if (swiper.allowSlideNext !== false) {
                    swiper.allowSlideNext = false;
                }
            }
        }
        
        // Apply scale/fade effect when close-swiping on last slide (to entire page)
        if (isCloseSwipingRef.current && effectiveCloseRef?.current && deltaX < 0) {
            // Prevent Swiper's default behavior
            if (event.cancelable) {
                event.preventDefault();
            }
            const absDelta = Math.abs(deltaX);
            const scale = 1 - Math.min(1, absDelta / SWIPE_CONFIG.SCALE_DISTANCE) * SWIPE_CONFIG.SCALE_MAX;
            const opacity = Math.max(0.2, 1 - 0.8 * Math.min(1, absDelta / SWIPE_CONFIG.SCALE_DISTANCE));
            effectiveCloseRef.current.style.transform = `translateX(${deltaX}px) scale(${scale})`;
            effectiveCloseRef.current.style.opacity = String(opacity);
        }
    }, [onCloseOnLastSlide, effectiveCloseRef, slidesData.length]);
    
    // Handle touch end to detect swipe past last slide
    const handleTouchEnd = useCallback((swiper, event) => {
        if (!touchStartRef.current) {
            touchStartRef.current = null;
            isCloseSwipingRef.current = false;
            horizontalLockRef.current = false;
            // Re-enable Swiper navigation if we weren't closing
            if (swiper && !swiper.isEnd) {
                swiper.allowSlideNext = true;
            }
            return;
        }
        
        // Swiper may pass PointerEvent or TouchEvent - handle both
        let endX, endY;
        if (event.type === 'pointerup' || event.type.startsWith('pointer')) {
            // PointerEvent - use clientX/clientY directly
            if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
                endX = event.clientX;
                endY = event.clientY;
            } else {
                touchStartRef.current = null;
                isCloseSwipingRef.current = false;
                horizontalLockRef.current = false;
                if (swiper && !swiper.isEnd) {
                    swiper.allowSlideNext = true;
                }
                return;
            }
        } else {
            // TouchEvent - get from changedTouches array
            const endTouchEvent = event.changedTouches?.[0] || event.targetTouches?.[0] || (event.originalEvent?.changedTouches?.[0]) || null;
            if (!endTouchEvent) {
                touchStartRef.current = null;
                isCloseSwipingRef.current = false;
                horizontalLockRef.current = false;
                if (swiper && !swiper.isEnd) {
                    swiper.allowSlideNext = true;
                }
                return;
            }
            endX = endTouchEvent.clientX;
            endY = endTouchEvent.clientY;
        }
        
        const deltaX = endX - touchStartRef.current.x;
        const isOnLastSlide = swiper.isEnd || (swiper.activeIndex >= slidesData.length - 1);
        
        // If we were close-swiping on the last slide
        if (isCloseSwipingRef.current && isOnLastSlide && onCloseOnLastSlide) {
            if (Math.abs(deltaX) >= SWIPE_CONFIG.MIN_SWIPE_CLOSE) {
                // Threshold reached - close
                onCloseOnLastSlide();
            } else {
                // Below threshold - animate back (entire page)
                if (effectiveCloseRef?.current) {
                    effectiveCloseRef.current.style.transition = `transform ${SWIPE_CONFIG.ANIMATE_MS}ms ease-out, opacity ${SWIPE_CONFIG.ANIMATE_MS}ms ease-out`;
                    effectiveCloseRef.current.style.transform = 'translateX(0) scale(1)';
                    effectiveCloseRef.current.style.opacity = '1';
                    animateBackTimeoutRef.current = setTimeout(() => {
                        if (effectiveCloseRef?.current) {
                            effectiveCloseRef.current.style.transition = '';
                        }
                        animateBackTimeoutRef.current = null;
                    }, SWIPE_CONFIG.ANIMATE_MS);
                }
                // Re-enable Swiper navigation
                swiper.allowSlideNext = true;
            }
        } else {
            // Re-enable Swiper navigation if we weren't closing
            if (!isOnLastSlide) {
                swiper.allowSlideNext = true;
            }
        }
        
        touchStartRef.current = null;
        isCloseSwipingRef.current = false;
        horizontalLockRef.current = false;
    }, [onCloseOnLastSlide, effectiveCloseRef, slidesData.length]);
    
    if (!slidesData?.length) return null;
    
    // Check if we're in editorial or spread mode (should show counter)
    const hasSpreadSlides = slidesData.some(slide => slide.type === 'spread');
    const shouldShowCounter = isEditorial || hasSpreadSlides;
    
    // Calculate counter values for ImageCounter
    // Exclude text slides from the count - only count image/spread slides
    const imageSlides = slidesData.filter(slide => slide.type !== 'text' && slide.type !== 'reading');
    const totalImages = flatImages.length || imageSlides.length;
    
    // Get current slide - use activeSlideIndex from swiper, fallback to prop
    const effectiveSlideIndex = activeSlideIndex !== null ? activeSlideIndex : (currentSlideIndex !== null ? currentSlideIndex : initialSlide);
    const currentSlide = effectiveSlideIndex >= 0 && effectiveSlideIndex < slidesData.length 
        ? slidesData[effectiveSlideIndex] 
        : null;
    
    // Calculate current position (1-indexed)
    // For editorial mode, subtract 1 if there's a text slide at the beginning
    let current = null;
    let currentEnd = null;
    
    if (currentSlide && currentSlide.type !== 'text' && currentSlide.type !== 'reading') {
        // Find the position of this slide among image slides only
        const imageSlideIndex = imageSlides.findIndex(slide => {
            if (currentSlide.type === 'spread') {
                return slide.type === 'spread' && 
                       slide.images?.[0]?.id === currentSlide.images?.[0]?.id;
            }
            return (slide.type === 'image' || slide.type === 'paper') && slide.id === currentSlide.id;
        });
        
        if (imageSlideIndex >= 0) {
            if (currentSlide.type === 'spread' && currentSlide.images?.length === 2) {
                // For spreads, calculate range based on flat images
                const leftId = currentSlide.images[0]?.id;
                const rightId = currentSlide.images[1]?.id;
                const leftIndex = flatImages.indexOf(leftId);
                const rightIndex = flatImages.indexOf(rightId);
                if (leftIndex >= 0 && rightIndex >= 0) {
                    current = Math.min(leftIndex, rightIndex) + 1; // 1-indexed
                    currentEnd = Math.max(leftIndex, rightIndex) + 1; // 1-indexed
                } else {
                    // Fallback: use slide index
                    current = imageSlideIndex + 1;
                }
            } else {
                // Single image - find in flat images if available
                const imageId = currentSlide.id || currentSlide.images?.[0]?.id;
                if (imageId && flatImages.length > 0) {
                    const flatIndex = flatImages.indexOf(imageId);
                    current = flatIndex >= 0 ? flatIndex + 1 : imageSlideIndex + 1;
                } else {
                    current = imageSlideIndex + 1;
                }
            }
        }
    }
    
    // Padding classes based on orientation
    // Landscape: minimal padding to maximize image size
    // Portrait: normal padding with room for UI
    const isSingleImageEditorialMode = isEditorial && !hasSpreadSlides;
    const imagePaddingClass = isSingleImageEditorialMode
        ? 'p-0'
        : isLandscape
            ? 'px-1 py-1'
            : 'px-2 py-14';
    
    return (
        <div ref={swipeWrapperRef} className="absolute inset-0 flex-1 flex" style={{ zIndex: 2 }}>
            {/* Left edge zone - allows native browser back gesture */}
            <div 
                className="absolute left-0 top-0 bottom-0 z-10"
                style={{ 
                    width: '3.5%',
                    touchAction: 'pan-x pan-y',
                }}
            />
            {/* Right edge zone - allows native browser forward gesture */}
            <div 
                className="absolute right-0 top-0 bottom-0 z-10"
                style={{ 
                    width: '3.5%',
                    touchAction: 'pan-x pan-y',
                }}
            />
            <Swiper
                key={isLandscape ? 'landscape' : 'portrait'}
                modules={[Virtual]}
                virtual
                onSwiper={(swiper) => { 
                    if (swiperRef) swiperRef.current = swiper;
                    swiperInstanceRef.current = swiper;
                    setActiveSlideIndex(swiper.activeIndex);
                    // Disable next navigation on last slide to allow close swipe
                    if (swiper.isEnd && onCloseOnLastSlide) {
                        swiper.allowSlideNext = false;
                    }
                    // Fire initial slide change to set counter
                    onSlideChange?.(swiper);
                }}
                onSlideChange={(swiper) => {
                    setActiveSlideIndex(swiper.activeIndex);
                    // Disable/enable next navigation based on position
                    if (swiper.isEnd && onCloseOnLastSlide) {
                        swiper.allowSlideNext = false;
                    } else {
                        swiper.allowSlideNext = true;
                    }
                    onSlideChange?.(swiper);
                }}
                onReachEnd={(swiper) => {
                    // Disable next navigation when reaching the end
                    if (onCloseOnLastSlide) {
                        swiper.allowSlideNext = false;
                    }
                }}
                resistance={true}
                resistanceRatio={0.85}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                initialSlide={initialSlide}
                slidesPerView={1}
                spaceBetween={0}
                className="absolute inset-0 flex-1 flex"
            >
                {slidesData.map((slide, slideIndex) => (
                    <SwiperSlide key={`${slide.id}-${isLandscape ? 'l' : 'p'}`} virtualIndex={slideIndex}>
                        {slide.type === 'text' ? (
                            <SlideshowText
                                placement="slide"
                                page={textPage}
                                pages={pages}
                                files={files}
                                theme={theme}
                                landscape={isLandscape}
                            />
                        ) : slide.type === 'reading' ? (
                            <SpreadReadingSlide
                                boxes={slide.boxes}
                                valign={slide.valign}
                                page={textPage}
                                pages={pages}
                                files={files}
                                theme={theme}
                            />
                        ) : slide.type === 'paper' ? (
                            <div className={`relative h-full w-full ${imagePaddingClass} bg-background`}>
                                <PictureWithBoxes slide={slide} ratio={pageDimensions?.aspectRatio || 2 / 3} />
                            </div>
                        ) : slide.type === 'spread' ? (
                            <SpreadSlide 
                                spreadData={slide}
                                alt={defaultAlt}
                                priority={slideIndex === effectiveSlideIndex}
                                gap={spreadGap}
                                pageDimensions={pageDimensions}
                                align={align}
                                fillScreen={fillScreen}
                                isMobile={true}
                                isLandscape={isLandscape}
                                showDescription
                            />
                        ) : (
                            <div 
                                className={`w-full h-full relative ${imagePaddingClass} bg-background`}
                                style={{ transition: `background-color ${FADE_DURATION}ms ease-in-out` }}
                            >
                                {/* Image centered in available space */}
                                <MediaFrame
                                    enabled={slideIndex === effectiveSlideIndex}
                                    className="w-full h-full flex items-center justify-center"
                                >
                                    {slide.boxes?.length > 0 ? (
                                        <PictureWithBoxes slide={slide}>
                                            <HeldPicture
                                                file={slide.file}
                                                alt={slide.file?.description || defaultAlt}
                                                priority={slideIndex === effectiveSlideIndex}
                                            />
                                        </PictureWithBoxes>
                                    ) : (
                                        <HeldPicture
                                            file={slide.file}
                                            alt={slide.file?.description || defaultAlt}
                                            priority={slideIndex === effectiveSlideIndex}
                                        />
                                    )}
                                </MediaFrame>
                                {/* Description at bottom of screen */}
                                <div className="absolute bottom-0 left-0 right-0 pb-4 text-center">
                                    <ImageCaption
                                        description={slide.file?.description || ''}
                                        imageId={slide.id}
                                        chrome={slideIndex === effectiveSlideIndex}
                                    />
                                </div>
                            </div>
                        )}
                    </SwiperSlide>
                ))}
            </Swiper>
            
            {/* Image counter - only show in editorial/spread mode, and not on text slides */}
            {shouldShowCounter && current && totalImages > 0 && currentSlide?.type !== 'text' && currentSlide?.type !== 'reading' && (
                <ImageCounter 
                    current={current}
                    currentEnd={currentEnd}
                    total={totalImages}
                    corner={counterCorner}
                />
            )}
        </div>
    );
});

TouchSwiper.displayName = 'TouchSwiper';

TouchSwiper.propTypes = {
    slidesData: PropTypes.array.isRequired,
    initialSlide: PropTypes.number.isRequired,
    onSlideChange: PropTypes.func.isRequired,
    defaultAlt: PropTypes.string,
    swipeWrapperRef: PropTypes.object,
    closeAnimationRef: PropTypes.object,
    pages: PropTypes.object,
    files: PropTypes.object,
    history: PropTypes.object,
    pageDimensions: PropTypes.object,
    /** Where spread slides sit in the screen: tl … br. */
    align: PropTypes.string,
    /** Spread slides fill the screen instead of keeping their ratio. */
    fillScreen: PropTypes.bool,
    spreadGap: PropTypes.number,
    isLandscape: PropTypes.bool,
    siteTitle: PropTypes.string,
    onCloseOnLastSlide: PropTypes.func,
    isEditorial: PropTypes.bool,
    currentSlideIndex: PropTypes.number,
    flatImages: PropTypes.array,
    /** The page whose slideshow text the text slide shows. */
    textPage: PropTypes.object,
    theme: PropTypes.object,
    counterCorner: PropTypes.oneOf(['tl', 'tr', 'bl', 'br', 'hidden']),
};

export default TouchSwiper;
